import { useState, useEffect, useRef } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useTicketCustomerHistory } from "@/pages/tickets/hooks/useTicketCustomerHistory";
import TicketHistoricThreadModal from "@/pages/tickets/components/TicketHistoricThreadModal";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";

function SectionToggle({ open, onClick, icon, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between px-4 py-3.5 text-left text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400/30 dark:text-slate-200 dark:hover:bg-slate-800/50"
    >
      <span className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
          <i className={`${icon} text-base`} aria-hidden />
        </span>
        {title}
      </span>
      <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-slate-400`} aria-hidden />
    </button>
  );
}

function matchReasonLabel(code) {
  if (code === "device") return "Same device / browser";
  if (code === "email") return "Shared email";
  return code;
}

export default function TicketingDetailsPanel({
  conversation,
  conversationLoading,
  workspaceId,
  selectedTicket,
  onChangeTicketGroup,
}) {
  const conv = conversation;
  const { activeWorkspace } = useWorkspace();
  const fromWorkspace =
    (conv?.workspaceName && String(conv.workspaceName).trim()) ||
    (activeWorkspace?.name && String(activeWorkspace.name).trim()) ||
    "—";
  const activeTicketId = selectedTicket?.id ?? null;
  const [historicModal, setHistoricModal] = useState(/** @type {{ sessionId: string, label: string } | null} */ (null));
  const [userDataOpen, setUserDataOpen] = useState(true);
  const [historicOpen, setHistoricOpen] = useState(true);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(true);
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const groupMenuRef = useRef(null);
  const { historicThreads, aiSummary, loading: historyLoading, error: historyError, refresh } = useTicketCustomerHistory(
    activeTicketId,
    workspaceId
  );

  useEffect(() => {
    setHistoricModal(null);
    setGroupMenuOpen(false);
  }, [activeTicketId]);

  useEffect(() => {
    if (!groupMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!groupMenuRef.current?.contains(event.target)) setGroupMenuOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setGroupMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [groupMenuOpen]);

  const selectedGroup = selectedTicket?.group === "refund" ? "refund" : selectedTicket?.group === "technical" ? "technical" : "technical";

  const handleGroupChange = async (nextGroup) => {
    if (!selectedTicket?.id || !onChangeTicketGroup || groupBusy) return;
    setGroupBusy(true);
    try {
      await onChangeTicketGroup({ ticketId: selectedTicket.id, group: nextGroup });
      setGroupMenuOpen(false);
    } catch (e) {
      surfaceInboxHttpError(e);
    } finally {
      setGroupBusy(false);
    }
  };

  if (conversationLoading && !conv) {
    return (
      <div className="ticketing-details-root items-center justify-center gap-3 px-4 text-center">
        <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
        <p className="text-xs font-medium text-slate-500">Loading profile…</p>
      </div>
    );
  }

  if (!conv) {
    return (
      <div className="ticketing-details-root items-center justify-center px-4 text-center">
        <p className="text-sm font-medium text-slate-500">Select a ticket to see visitor details.</p>
      </div>
    );
  }

  return (
    <div className="ticketing-details-root">
      <div className="ticketing-details-scroll">
        <div>
          <div className="border-b border-slate-100">
            <SectionToggle open={userDataOpen} onClick={() => setUserDataOpen(!userDataOpen)} icon="ri-user-line" title="Profile" />
            {userDataOpen && (
              <div className="space-y-2.5 px-4 pb-4">
                {[
                  { label: "Name", value: conv.user.name },
                  { label: "Company", value: conv.user.company },
                  { label: "Type", value: conv.user.type },
                  { label: "Location", value: conv.user.location },
                  { label: "Email", value: conv.user.email },
                  { label: "From Workspace", value: fromWorkspace },
                ].map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-3 text-xs">
                    <span className="shrink-0 font-medium text-slate-400">{row.label}</span>
                    <span className="text-right font-semibold text-slate-800">{row.value}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="shrink-0 font-medium text-slate-400">Group</span>
                  <div ref={groupMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setGroupMenuOpen((v) => !v)}
                      disabled={!selectedTicket || !onChangeTicketGroup || groupBusy}
                      aria-haspopup="menu"
                      aria-expanded={groupMenuOpen}
                      className="inline-flex h-8 min-w-[146px] items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-800 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            selectedGroup === "refund" ? "bg-amber-500 shadow-sm shadow-amber-400/60" : "bg-sky-500 shadow-sm shadow-sky-400/60"
                          }`}
                        />
                        {selectedGroup === "refund" ? "Refund" : "Technical"}
                      </span>
                      <i className={`ri-arrow-down-s-line text-sm text-slate-500 transition-transform ${groupMenuOpen ? "rotate-180" : ""}`} aria-hidden />
                    </button>

                    {groupMenuOpen && (
                      <div className="absolute right-0 z-30 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                        <button
                          type="button"
                          onClick={() => void handleGroupChange("refund")}
                          disabled={groupBusy}
                          className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-amber-50 disabled:opacity-50 ${
                            selectedGroup === "refund" ? "bg-amber-50/70" : ""
                          }`}
                        >
                          <span className="mt-1 h-2 w-2 rounded-full bg-amber-500" />
                          <span className="min-w-0">
                            <span className="block text-xs font-semibold text-slate-800">Refund</span>
                            <span className="block text-[11px] text-slate-500">Billing, payments, renewals</span>
                          </span>
                          {selectedGroup === "refund" && <i className="ri-check-line ml-auto text-sm text-amber-700" aria-hidden />}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleGroupChange("technical")}
                          disabled={groupBusy}
                          className={`flex w-full items-start gap-2.5 border-t border-slate-100 px-3 py-2.5 text-left transition-colors hover:bg-sky-50 disabled:opacity-50 ${
                            selectedGroup === "technical" ? "bg-sky-50/70" : ""
                          }`}
                        >
                          <span className="mt-1 h-2 w-2 rounded-full bg-sky-500" />
                          <span className="min-w-0">
                            <span className="block text-xs font-semibold text-slate-800">Technical</span>
                            <span className="block text-[11px] text-slate-500">Product bugs and integrations</span>
                          </span>
                          {selectedGroup === "technical" && <i className="ri-check-line ml-auto text-sm text-sky-700" aria-hidden />}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-b border-slate-100 dark:border-slate-700/60">
            <SectionToggle open={historicOpen} onClick={() => setHistoricOpen(!historicOpen)} icon="ri-chat-history-line" title="Historic tickets" />
            {historicOpen && (
              <div className="space-y-2 px-4 pb-4">
                {historyLoading && <p className="text-xs font-medium text-slate-500">Loading history…</p>}
                {historyError && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/80 px-2 py-1.5 text-xs text-rose-800">
                    <span>{historyError.message}</span>
                    <button type="button" onClick={refresh} className="font-semibold underline">
                      Retry
                    </button>
                  </div>
                )}
                {!historyLoading && !historyError && historicThreads.length === 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">No previous tickets for this customer.</p>
                )}
                {!historyLoading &&
                  historicThreads.map((thread) => (
                    <div key={thread.id} className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 ring-1 ring-slate-100/80 dark:border-slate-700/60 dark:bg-slate-800/40 dark:ring-slate-700/40">
                      <div className="mb-1.5 flex flex-wrap gap-1">
                          {thread.matchReasons.map((code) => (
                            <span
                              key={code}
                              className="rounded-md bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200/80 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600/60"
                            >
                              {matchReasonLabel(code)}
                            </span>
                          ))}
                      </div>
                      <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                        Ticket {thread.id} · {thread.startedAtLabel}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-800 dark:text-slate-200">{thread.preview}</p>
                      {thread.id ? (
                        <button
                          type="button"
                          onClick={() => setHistoricModal({ sessionId: String(thread.id), label: String(thread.startedAtLabel || thread.preview || "").slice(0, 80) })}
                          className="mt-2 text-xs font-semibold text-teal-600 hover:text-teal-500"
                        >
                          View transcript
                        </button>
                      ) : null}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="border-b border-slate-100 dark:border-slate-700/60">
            <SectionToggle open={aiSummaryOpen} onClick={() => setAiSummaryOpen(!aiSummaryOpen)} icon="ri-sparkling-2-line" title="Customer summary (AI)" />
            {aiSummaryOpen && (
              <div className="space-y-2 px-4 pb-4">
                {!historyLoading && aiSummary?.summary ? (
                  <p className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2.5 text-xs leading-relaxed text-slate-800 dark:border-teal-800/50 dark:bg-teal-950/40 dark:text-slate-200">{aiSummary.summary}</p>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-slate-400">Summary unavailable.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <TicketHistoricThreadModal
        open={Boolean(historicModal?.sessionId)}
        ticketId={historicModal?.sessionId ?? ""}
        workspaceId={workspaceId}
        previewLabel={historicModal?.label ?? ""}
        onClose={() => setHistoricModal(null)}
      />
    </div>
  );
}
